import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import _ from 'underscore'

import Modal from 'components/Modal'
import { Button, type ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'

import Brand from 'helpers/brand'

import useDealer from 'hooks/useDealer'
import { useSessionContext } from 'hooks/SessionContext'

import { StackedButtonContainer, screenSize } from 'styles/shared'

import Benefits from 'pages/paywall/Benefits'

type Props = {
  closeCallback: (reloadPage) => void
}

const UpgradeModal = ({ closeCallback }: Props) => {
  const { session } = useSessionContext()
  const navigate = useNavigate()
  const { dealer, isDealerLoaded } = useDealer()
  const { imageUrl } = dealer

  const onClickClose = () => {
    closeCallback(false)
  }

  const onDealerLogoPress = () => {
    closeCallback(false)
    navigate('/dealer_image')
  }

  let uploadText = 'Upload Dealer Logo'

  const getBenefits = useCallback(() => {
    if (session.isTechnician) {
      return _.select(Benefits.PREMIUM, (benefit) => benefit !== Benefits.SYSTEM_REPORTS_EMAIL_PREMIUM_BENEFIT)
    } else {
      return Benefits.PREMIUM
    }
  }, [session.isTechnician])

  if (isDealerLoaded) {
    if (imageUrl !== null) {
      uploadText = 'Update Dealer Logo'
    }
  } else {
    return
  }

  return (
    <S.Modal variant="sm" isDisplayed closeCallback={() => closeCallback(false)}>
      <S.Top>
        <S.ProductLogo src={Brand.premiumProductLogoPath()} alt={`${Brand.brandName()} logo`} />
        <div />
      </S.Top>

      <S.Success>
        Success!
      </S.Success>
      <p>Everyone on your account now has access to all premium features. Take a moment to get to know Diagnostics’ new features!</p>

      <ul>
        {getBenefits().map(benefit => (
          <S.Explanation key={benefit}>{benefit}</S.Explanation>
        ))}
      </ul>

      <S.StackedButtonContainer noBorder>
        <Button
          decoration="secondary"
          text="OK"
          styles={closeButtonStyle}
          onPress={onClickClose}
        />
        <Button
          decoration="positive"
          text={uploadText}
          styles={uploadButtonStyle}
          onPress={onDealerLogoPress}
        />
      </S.StackedButtonContainer>
    </S.Modal>
  )
}

export default UpgradeModal

const buttonFontSize = 14
const buttonHeight = 44
const closeButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    borderWidth: 0,
    height: buttonHeight
  },
  text: {
    fontSize: buttonFontSize,
    textTransform: 'none'
  }
}

const uploadButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    marginLeft: 15,
    height: buttonHeight
  },
  text: {
    fontSize: buttonFontSize,
    textTransform: 'none'
  }
}

const S = {
  Modal: styled(Modal)`
    height: 100%;
    display: flex;
    flex-direction: column;
  `,
  Explanation: styled.li`
    margin: 10px 0 0 0;
    padding-right: 5px;
    padding-left: 5px;
  `,
  ProductLogo: styled.img`
    height: 50px;
    @media ${screenSize.small} {
      max-width: 290px;
  `,
  Success: styled.div`
    font-size: 18px;
    font-weight: bold;
    color: #2C7AC2;
    text-transform: none;
  `,
  Top: styled.div`
    display: flex;
    justify-content: center;
    padding: 10px;
  `,
  StackedButtonContainer: styled(StackedButtonContainer)`
    margin-top: auto;
  `
}
